import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { RootDispatch, RootState } from "../../store";
import { getAllBOMs, setSelectedBOM } from "../../store/slices/bomSlice";
import { useNavigate } from "react-router-dom";
import { format } from "date-fns";
import Button, { ButtonType } from "../../components/Assets/Button";
import CreateBOM from "./CreateBOM";
import { IBOM } from "../../models/bom.model";
import SearchInput from "../../components/Search/SearchInput";

const BOMList = () => {
  const dispatch = useDispatch<RootDispatch>();
  const { boms, loading } = useSelector(
    (state: RootState) => state.billOfMaterials,
  );
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isEdit, setIsEdit] = useState(false);

  const navigate = useNavigate();

  const handleDuplicateBOM = (bom: IBOM) => {
    setIsEdit(true);
    setIsCreateModalOpen(true);
    dispatch(setSelectedBOM(bom));
  };
  const handleViewBOM = (bom: IBOM) => {
    dispatch(setSelectedBOM(bom));
    navigate(`/boms/${bom._id}`, {
      state: { from: "boms" },
    });
  };

  useEffect(() => {
    dispatch(getAllBOMs());
  }, [dispatch]);

  return (
    <div className="container mx-auto px-4 py-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-8 gap-4">
        <div className="relative w-full">
          <SearchInput
            placeholder="Search BOMs by name..."
            className="w-full px-4 py-2 text-xs border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            onSearch={(term) => {
              dispatch(getAllBOMs(term));
            }}
          />
        </div>

        <Button
          type={ButtonType.Submit}
          onClick={() => setIsCreateModalOpen(true)}
          className="flex w-full md:w-auto md:flex-none text-xs bg-navyBlue-primary hover:bg-navyBlue-light text-white px-4 py-2 rounded-md shadow-md transition-colors"
        >
          Create New BOM
        </Button>
      </div>

      {isCreateModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-[110]">
          <div className="bg-white rounded-xl shadow-lg w-full max-w-4xl max-h-[90vh] overflow-y-auto">
            <div className="p-6 flex justify-between items-center">
              <h2 className="text-lg font-semibold">
                Create New Bill of Materials
              </h2>
              <button
                onClick={() => {
                  setIsCreateModalOpen(false);
                  setIsEdit(false);
                }}
                className="text-gray-500 hover:text-gray-700"
              >
                ×
              </button>
            </div>
            <CreateBOM
              isEdit={isEdit}
              onSuccess={() => {
                setIsCreateModalOpen(false);
                setIsEdit(false);
                dispatch(getAllBOMs());
              }}
              onCancel={() => {
                setIsCreateModalOpen(false);
                setIsEdit(false);
              }}
            />
          </div>
        </div>
      )}

      <div className="hidden md:block bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
        {loading && (
          <div className="absolute inset-0 bg-white/70 backdrop-blur-sm flex items-center justify-center z-10">
            <div className="flex flex-col items-center gap-2">
              <div className="w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
              <span className="text-sm font-medium text-gray-600">
                Loading...
              </span>
            </div>
          </div>
        )}
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Name
                </th>
                <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Description
                </th>
                <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Composition
                </th>
                <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Created
                </th>
                <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200 text-xs">
              {boms.map((bom: IBOM) => (
                <tr
                  key={bom._id}
                  className="hover:bg-gray-50 transition-colors"
                >
                  <td className="px-6 py-4 whitespace-nowrap">
                    <button
                      className="font-medium text-gray-900"
                      onClick={() => handleViewBOM(bom)}
                    >
                      {bom.name}
                    </button>
                  </td>
                  <td className="px-6 py-4">
                    <div className="text-gray-500 line-clamp-1">
                      {bom.description || "No description"}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex flex-col gap-1">
                      <span className="px-3 py-1 bg-gray-100 text-gray-800 rounded-full w-fit">
                        {bom.items.length} Items
                      </span>
                      <div className="text-[10px] text-gray-400">
                        {bom.items.filter((i) => i.type === "product").length}{" "}
                        Prod |{" "}
                        {bom.items.filter((i) => i.type === "material").length}{" "}
                        Mat
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-gray-500">
                      {format(new Date(bom.createdAt || ""), "MMM dd, yyyy")}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-left">
                    <BomActions
                      onView={handleViewBOM}
                      onDuplicate={handleDuplicateBOM}
                      selectedBOM={bom}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="md:hidden space-y-4">
        {boms.map((bom: IBOM) => (
          <div
            key={bom._id}
            className="bg-white p-5 rounded-xl shadow-sm border border-gray-100"
          >
            <div className="flex justify-between items-start">
              <h3 className="font-bold text-lg text-gray-800">{bom.name}</h3>
              <div className="flex flex-col items-end gap-1">
                <span className="px-2 py-1 bg-gray-100 text-gray-800 rounded-full text-[10px]">
                  {bom.items.length} Items
                </span>
              </div>
            </div>
            <p className="text-gray-600 mt-1 text-sm line-clamp-2">
              {bom.description || "No description"}
            </p>
            <div className="flex justify-between items-center mt-4">
              <span className="text-gray-500 text-sm">
                {format(new Date(bom.createdAt || ""), "MMM dd, yyyy")}
              </span>
              <BomActions
                onView={handleViewBOM}
                onDuplicate={handleDuplicateBOM}
                selectedBOM={bom}
              />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default BOMList;

type BomActionsProps = {
  onDuplicate: (bom: IBOM) => void;
  onView: (bom: IBOM) => void;
  className?: string;
  selectedBOM: IBOM;
};

const BomActions: React.FC<BomActionsProps> = ({
  onDuplicate,
  className = "",
  selectedBOM,
  onView,
}) => {
  return (
    <div className={`inline-flex items-center ${className}`}>
      {/* View Link */}
      <button
        onClick={() => onView(selectedBOM)}
        className="text-blue-600 hover:text-blue-800 mr-4"
      >
        View
      </button>

      {/* Duplicate Action */}
      <button
        onClick={() => onDuplicate(selectedBOM)}
        className="text-blue-600 hover:text-blue-800"
      >
        Duplicate
      </button>
    </div>
  );
};
